import { supabase } from './supabase'
import Papa from '../utils/papaparse-wrapper'
import { findExistingClient } from './clientLookup'
import { saveExaminationTransaction } from './transactions'

const CSV_HEADERS = [
  'first_name',
  'last_name',
  'email',
  'phone',
  'date_of_birth',
  'gender',
  'current_medications',
  'allergies',
  'past_surgeries',
  'family_history',
  'chronic_conditions',
  'diet',
  'exercise',
  'sleep',
  'custom_habits',
  'stress_level',
  'alcohol',
  'smoking',
  'primary_goals',
  'expectations',
  'previous_treatments',
  'blood_pressure',
  'heart_rate',
  'respiratory_rate',
  'temperature',
  'oxygen_saturation',
  'weight',
  'height',
  'bmi',
  'custom_questions_json',
  'examination_date',
]

const formatHabitForCsv = (value) => {
  if (value == null || value === '') return ''
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

const parseHabitFromCsv = (value) => {
  if (value == null || String(value).trim() === '') return null
  const text = String(value).trim()
  if (text.startsWith('[') || text.startsWith('{')) {
    try {
      return JSON.parse(text)
    } catch {
      return text
    }
  }
  // Semicolon-separated habits → array of { text }
  if (text.includes(';')) {
    return text
      .split(';')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => ({ id: crypto.randomUUID?.() || String(Date.now()), text: part, tagged: false }))
  }
  return text
}

const parseQuestionsFromCsv = (value) => {
  if (value == null || String(value).trim() === '') return []
  try {
    const parsed = JSON.parse(String(value))
    if (Array.isArray(parsed)) {
      return parsed
        .filter((q) => q && q.prompt)
        .map((q) => ({
          prompt: String(q.prompt),
          type: q.type === 'choice' ? 'choice' : 'text',
          answer: q.answer != null ? String(q.answer) : '',
        }))
    }
  } catch {
    // ignore
  }
  return []
}

const downloadBlob = (content, fileName, mimeType) => {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

const examToCsvRow = (exam, client, history) => {
  const vitals = exam.vital_signs || {}
  return {
    first_name: client?.first_name || '',
    last_name: client?.last_name || '',
    email: client?.email || '',
    phone: client?.phone || '',
    date_of_birth: client?.date_of_birth || '',
    gender: client?.gender || '',
    current_medications: history?.current_medications || '',
    allergies: history?.allergies || '',
    past_surgeries: history?.past_surgeries || '',
    family_history: history?.family_history || '',
    chronic_conditions: history?.chronic_conditions || '',
    diet: formatHabitForCsv(history?.diet),
    exercise: formatHabitForCsv(history?.exercise),
    sleep: formatHabitForCsv(history?.sleep),
    custom_habits: formatHabitForCsv(history?.custom_habits),
    stress_level: history?.stress_level || '',
    alcohol: history?.alcohol || '',
    smoking: history?.smoking || '',
    primary_goals: history?.primary_goals || '',
    expectations: history?.expectations || '',
    previous_treatments: history?.previous_treatments || '',
    blood_pressure: vitals.bloodPressure || '',
    heart_rate: vitals.heartRate || '',
    respiratory_rate: vitals.respiratoryRate || '',
    temperature: vitals.temperature || '',
    oxygen_saturation: vitals.oxygenSaturation || '',
    weight: vitals.weight || '',
    height: vitals.height || '',
    bmi: vitals.bmi || '',
    custom_questions_json: JSON.stringify(exam.custom_questions || []),
    examination_date: exam.created_at || '',
  }
}

/**
 * Download a blank CSV template matching examination + client fields.
 */
export const downloadExaminationCsvTemplate = () => {
  const example = {
    first_name: 'Jane',
    last_name: 'Doe',
    email: 'jane@example.com',
    phone: '555-0100',
    date_of_birth: '1985-04-12',
    gender: 'Female',
    current_medications: '',
    allergies: 'Penicillin',
    past_surgeries: '',
    family_history: '',
    chronic_conditions: '',
    diet: '[{"text":"Vegetarian","tagged":true}]',
    exercise: '[{"text":"Walking 3x/week","tagged":false}]',
    sleep: '[{"text":"7 hours","tagged":false}]',
    custom_habits: '',
    stress_level: 'Moderate',
    alcohol: 'Occasional',
    smoking: 'Non-smoker',
    primary_goals: 'Improve energy',
    expectations: '',
    previous_treatments: '',
    blood_pressure: '118/76',
    heart_rate: '72',
    respiratory_rate: '16',
    temperature: '36.6',
    oxygen_saturation: '98',
    weight: '68',
    height: '165',
    bmi: '25.0',
    custom_questions_json: '[{"prompt":"Any recent travel?","type":"choice","answer":"No"}]',
    examination_date: new Date().toISOString().slice(0, 10),
  }

  const csv = Papa.unparse({
    fields: CSV_HEADERS,
    data: [example],
  })
  downloadBlob(csv, `examination-import-template.csv`, 'text/csv;charset=utf-8;')
  return { success: true }
}

/**
 * Export current form values as one CSV row (offline transfer).
 */
export const exportExaminationFormToCSV = (formValues = {}, customQuestions = []) => {
  const row = {
    first_name: formValues.firstName || '',
    last_name: formValues.lastName || '',
    email: formValues.email || '',
    phone: formValues.phone || '',
    date_of_birth: formValues.dateOfBirth || '',
    gender: formValues.gender || '',
    current_medications: formValues.currentMedications || '',
    allergies: formValues.allergies || '',
    past_surgeries: formValues.pastSurgeries || '',
    family_history: formValues.familyHistory || '',
    chronic_conditions: formValues.chronicConditions || '',
    diet: formatHabitForCsv(formValues.diet),
    exercise: formatHabitForCsv(formValues.exercise),
    sleep: formatHabitForCsv(formValues.sleep),
    custom_habits: formatHabitForCsv(formValues.customHabits),
    stress_level: formValues.stressLevel || '',
    alcohol: formValues.alcohol || '',
    smoking: formValues.smoking || '',
    primary_goals: formValues.primaryGoals || '',
    expectations: formValues.expectations || '',
    previous_treatments: formValues.previousTreatments || '',
    blood_pressure: formValues.bloodPressure || '',
    heart_rate: formValues.heartRate || '',
    respiratory_rate: formValues.respiratoryRate || '',
    temperature: formValues.temperature || '',
    oxygen_saturation: formValues.oxygenSaturation || '',
    weight: formValues.weight || '',
    height: formValues.height || '',
    bmi: formValues.bmi || '',
    custom_questions_json: JSON.stringify(
      (customQuestions || [])
        .filter((q) => q?.prompt)
        .map((q) => ({ prompt: q.prompt, type: q.type || 'text', answer: q.answer || '' }))
    ),
    examination_date: new Date().toISOString(),
  }

  const csv = Papa.unparse({ fields: CSV_HEADERS, data: [row] })
  const name = `${row.last_name || 'exam'}-${row.first_name || 'client'}`.toLowerCase().replace(/[^a-z0-9]+/g, '-')
  downloadBlob(csv, `examination-${name}-${Date.now()}.csv`, 'text/csv;charset=utf-8;')
  return { success: true }
}

/**
 * Export saved examinations (optionally for one client) to CSV.
 */
export const exportExaminationsToCSV = async (organizationId, clientId = null) => {
  let query = supabase
    .from('examinations')
    .select('*, clients(*), medical_history:medical_history_id(*)')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })

  if (clientId) {
    query = query.eq('client_id', clientId)
  }

  const { data, error } = await query
  if (error) throw error

  const rows = (data || []).map((exam) =>
    examToCsvRow(exam, exam.clients, exam.medical_history)
  )

  const csv = Papa.unparse({ fields: CSV_HEADERS, data: rows })
  downloadBlob(
    csv,
    `examinations-export-${clientId || 'all'}-${Date.now()}.csv`,
    'text/csv;charset=utf-8;'
  )
  return { success: true, count: rows.length }
}

/**
 * Parse examination CSV file into row objects.
 */
export const parseExaminationCsvFile = (file) =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => resolve(results.data || []),
      error: (error) => reject(error),
    })
  })

const normalizeHeaderKey = (key) =>
  String(key || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')

const getRowValue = (row, field) => {
  if (row[field] != null && row[field] !== '') return row[field]
  const match = Object.keys(row).find((k) => normalizeHeaderKey(k) === field)
  return match ? row[match] : ''
}

/**
 * Import examination CSV rows into clients + medical histories + examinations.
 */
export const importExaminationsFromCsv = async (organizationId, file, userId = null) => {
  const rawRows = await parseExaminationCsvFile(file)
  if (!rawRows.length) {
    throw new Error('CSV file is empty')
  }

  const results = {
    total: rawRows.length,
    imported: 0,
    skipped: 0,
    errors: [],
  }

  for (let i = 0; i < rawRows.length; i++) {
    const row = rawRows[i]
    const firstName = String(getRowValue(row, 'first_name') || '').trim()
    const lastName = String(getRowValue(row, 'last_name') || '').trim()
    const dateOfBirth = String(getRowValue(row, 'date_of_birth') || '').trim() || null
    const primaryGoals = String(getRowValue(row, 'primary_goals') || '').trim()
    const bloodPressure = String(getRowValue(row, 'blood_pressure') || '').trim()
    const heartRate = String(getRowValue(row, 'heart_rate') || '').trim()

    if (!firstName || !lastName) {
      results.skipped += 1
      results.errors.push({ row: i + 2, error: 'Missing first_name or last_name' })
      continue
    }

    if (!primaryGoals) {
      results.skipped += 1
      results.errors.push({ row: i + 2, error: 'Missing primary_goals' })
      continue
    }

    if (!bloodPressure || !heartRate) {
      results.skipped += 1
      results.errors.push({ row: i + 2, error: 'Missing blood_pressure or heart_rate' })
      continue
    }

    try {
      const existingClient = await findExistingClient(
        organizationId,
        firstName,
        lastName,
        dateOfBirth
      )

      const vitalSigns = {
        bloodPressure,
        heartRate,
        respiratoryRate: String(getRowValue(row, 'respiratory_rate') || '').trim() || null,
        temperature: String(getRowValue(row, 'temperature') || '').trim() || null,
        oxygenSaturation: String(getRowValue(row, 'oxygen_saturation') || '').trim() || null,
        weight: String(getRowValue(row, 'weight') || '').trim() || null,
        height: String(getRowValue(row, 'height') || '').trim() || null,
        bmi: String(getRowValue(row, 'bmi') || '').trim() || null,
      }

      const customQuestions = parseQuestionsFromCsv(getRowValue(row, 'custom_questions_json'))

      const result = await saveExaminationTransaction(
        organizationId,
        {
          client: {
            id: existingClient?.id || null,
            first_name: firstName,
            last_name: lastName,
            date_of_birth: dateOfBirth,
            gender: String(getRowValue(row, 'gender') || '').trim() || null,
            phone: String(getRowValue(row, 'phone') || '').trim() || null,
            email: String(getRowValue(row, 'email') || '').trim() || null,
          },
          medicalHistory: {
            id: existingClient?.medical_history?.id || null,
            organization_id: organizationId,
            current_medications: String(getRowValue(row, 'current_medications') || '').trim() || null,
            allergies: String(getRowValue(row, 'allergies') || '').trim() || null,
            past_surgeries: String(getRowValue(row, 'past_surgeries') || '').trim() || null,
            family_history: String(getRowValue(row, 'family_history') || '').trim() || null,
            chronic_conditions: String(getRowValue(row, 'chronic_conditions') || '').trim() || null,
            diet: parseHabitFromCsv(getRowValue(row, 'diet')),
            exercise: parseHabitFromCsv(getRowValue(row, 'exercise')),
            sleep: parseHabitFromCsv(getRowValue(row, 'sleep')),
            custom_habits: parseHabitFromCsv(getRowValue(row, 'custom_habits')),
            stress_level: String(getRowValue(row, 'stress_level') || '').trim() || null,
            alcohol: String(getRowValue(row, 'alcohol') || '').trim() || null,
            smoking: String(getRowValue(row, 'smoking') || '').trim() || null,
            primary_goals: primaryGoals,
            expectations: String(getRowValue(row, 'expectations') || '').trim() || null,
            previous_treatments: String(getRowValue(row, 'previous_treatments') || '').trim() || null,
          },
          vitalSigns,
          customQuestions,
          examinationId: null,
        },
        userId
      )

      if (!result.success) {
        throw new Error('Transaction failed')
      }
      results.imported += 1
    } catch (error) {
      results.skipped += 1
      results.errors.push({ row: i + 2, error: error.message || 'Import failed' })
    }
  }

  return results
}

export const getExaminationCsvFields = () =>
  CSV_HEADERS.map((value) => ({
    value,
    label: value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    required: ['first_name', 'last_name', 'primary_goals', 'blood_pressure', 'heart_rate'].includes(value),
  }))
